import React from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  TextField,
  Checkbox,
  Paper,
  Typography,
  useTheme,
  alpha,
  CircularProgress,
} from "@mui/material";
import MDBox from "components/MDBox";

// Dropdown paper that renders a header above the options list
const HeaderPaper = React.forwardRef(function HeaderPaper({ header, children, ...rest }, ref) {
  return (
    <Paper ref={ref} {...rest}>
      {/* Keep the input focused so clicking the header doesn't close the dropdown */}
      <MDBox onMouseDown={(event) => event.preventDefault()}>{header}</MDBox>
      {children}
    </Paper>
  );
});

HeaderPaper.propTypes = {
  header: PropTypes.node,
  children: PropTypes.node,
};

/**
 * CustomAutocomplete - A flexible, reusable autocomplete component for select dropdowns
 *
 * @param {Array} options - Array of options to display in the dropdown
 * @param {Function} getOptionLabel - Function to extract label from option object. Defaults to option => option
 * @param {Function} getOptionValue - Function to extract value from option object. Defaults to option => option
 * @param {*} value - Currently selected value(s)
 * @param {Function} onChange - Callback when selection changes. Receives (event, newValue)
 * @param {string} label - Label for the input field
 * @param {string} placeholder - Placeholder text
 * @param {boolean} multiple - Enable multiple selection
 * @param {boolean} disabled - Disable the component
 * @param {boolean} freeSolo - Allow free text input
 * @param {boolean} disableClearable - Hide the clear button
 * @param {boolean} showCheckbox - Show checkboxes for multiple selection
 * @param {string} height - Height of the input field (default: "40px")
 * @param {number} maxHeight - Max height of dropdown menu (default: 350)
 * @param {string} noOptionsText - Text to show when no options available
 * @param {string} loadingText - Text to show while loading
 * @param {boolean} loading - Show loading state
 * @param {Function} isOptionEqualToValue - Function to determine if option equals value
 * @param {Function} filterOptions - Custom filter function
 * @param {Function} renderOption - Custom render function for options
 * @param {Object} sx - Additional sx styles for the root component
 * @param {Object} textFieldProps - Additional props to pass to TextField
 * @param {Object} listboxProps - Additional props for the listbox
 * @param {ReactNode} listHeader - Content rendered at the top of the dropdown, above the options
 * @param {string} variant - TextField variant (outlined, filled, standard)
 * @param {string} size - Size of the component (small, medium)
 * @param {boolean} fullWidth - Make component full width
 * @param {boolean} required - Mark field as required
 * @param {string} error - Error message to display
 * @param {string} helperText - Helper text to display below input
 * @example
 * Simple usage with array of strings
 * <CustomAutocomplete
 *   options={["Option 1", "Option 2", "Option 3"]}
 *   value={selectedValue}
 *   onChange={(e, newValue) => setSelectedValue(newValue)}
 *   label="Select Option"
 * />
 *
 * @example
 * Usage with array of objects
 * <CustomAutocomplete
 *   options={[
 *     { id: 1, name: "John" },
 *     { id: 2, name: "Jane" }
 *   ]}
 *   getOptionLabel={(option) => option.name}
 *   getOptionValue={(option) => option.id}
 *   value={selectedUser}
 *   onChange={(e, newValue) => setSelectedUser(newValue)}
 *   label="Select User"
 *   isOptionEqualToValue={(option, value) => option.id === value.id}
 * />
 *
 * @example
 * // Multiple selection with checkboxes
 * <CustomAutocomplete
 *   options={subjects}
 *   getOptionLabel={(option) => option.name}
 *   value={selectedSubjects}
 *   onChange={(e, newValue) => setSelectedSubjects(newValue)}
 *   label="Select Subjects"
 *   multiple
 *   showCheckbox
 * />
 */
const CustomAutocomplete = ({
  options = [],
  getOptionLabel = (option) => option,
  getOptionValue = (option) => option,
  value,
  onChange,
  label = "",
  placeholder = "",
  multiple = false,
  disabled = false,
  freeSolo = false,
  disableClearable = false,
  showCheckbox = false,
  height = "40px",
  maxHeight = 350,
  noOptionsText = "No options available",
  loadingText = "Loading...",
  loading = false,
  isOptionEqualToValue,
  filterOptions,
  renderOption,
  renderTags,
  getOptionDisabled,
  groupBy,
  renderGroup,
  sx = {},
  textFieldProps = {},
  listboxProps = {},
  listHeader,
  variant = "outlined",
  size = "medium",
  fullWidth = true,
  required = false,
  error = false,
  helperText = "",
  width = "",
}) => {
  // Default isOptionEqualToValue if not provided
  const defaultIsOptionEqualToValue = (option, val) => {
    if (!option || !val) return option === val;
    const optionValue = getOptionValue(option);
    const valueToCompare = getOptionValue(val);
    return optionValue === valueToCompare;
  };
  const theme = useTheme();

  // Default renderOption with optional checkbox
  const defaultRenderOption = (listProps, option, { selected }) => {
    const { key: labelKey, ...optionProps } = listProps;
    // MUI keys options by label, so options sharing a label collide; prefer the option's value
    const optionValue = getOptionValue(option);
    const key =
      typeof optionValue === "string" || typeof optionValue === "number" ? optionValue : labelKey;
    if (option.isButton) {
      return React.createElement(
        "li",
        {
          ...optionProps,
          key,
          style: {
            ...optionProps.style,
            padding: 0,
            marginTop: 2,
            marginBottom: 2,
          },
        },
        <Typography
          variant="body3"
          sx={{
            width: "100%",
            py: 2,
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            backgroundColor: alpha(theme.palette.info.main, 0.8),
            color: theme.palette.info.contrastText,
            fontWeight: 500,
            padding: 0.5,
            borderRadius: 1,
            transition: "background-color 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: theme.palette.info.main,
            },
          }}
        >
          {getOptionLabel(option)}
        </Typography>,
      );
    }

    // Default rendering for normal options
    return React.createElement(
      "li",
      { ...optionProps, key },
      showCheckbox && multiple && (
        <Checkbox checked={selected} sx={{ marginRight: 0, marginLeft: -0.5 }} />
      ),
      <Typography variant="subtitle2" fontSize="0.9rem">
        {getOptionLabel(option)}
      </Typography>,
    );
  };
  const hasMultipleValue = multiple && Array.isArray(value) && value.length > 0;

  return (
    <Autocomplete
      options={options}
      getOptionLabel={getOptionLabel}
      value={value}
      onChange={onChange}
      multiple={multiple}
      disabled={disabled}
      freeSolo={freeSolo}
      disableCloseOnSelect={multiple}
      disableClearable={disableClearable}
      getOptionDisabled={getOptionDisabled}
      loading={loading}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      isOptionEqualToValue={isOptionEqualToValue || defaultIsOptionEqualToValue}
      filterOptions={filterOptions}
      groupBy={groupBy}
      renderGroup={renderGroup}
      renderOption={renderOption || defaultRenderOption}
      renderTags={renderTags}
      PaperComponent={listHeader ? HeaderPaper : undefined}
      slotProps={listHeader ? { paper: { header: listHeader } } : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={hasMultipleValue ? "" : placeholder}
          variant={variant}
          size={size}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} thickness={5} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            "& .MuiInputBase-root": {
              height: height,
              display: "flex",
              alignItems: "center",
              paddingTop: "0 !important",
              paddingBottom: "0 !important",
              paddingRight: "0 !important",
              paddingLeft: "12px !important",
            },
            "& .MuiInputBase-input": {
              padding: "0 !important",
              display: "flex",
              alignItems: "center",
            },
            ...(error
              ? {
                  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                    borderColor: `${theme.palette.error.main} !important`,
                    borderWidth: "1px",
                  },
                }
              : {}),
            ...textFieldProps.sx,
          }}
          {...textFieldProps}
        />
      )}
      ListboxProps={{
        className: `custom-scrollbar ${listboxProps.className || ""}`.trim(),
        sx: {
          maxHeight: maxHeight,
          "& .MuiAutocomplete-option": {
            paddingLeft: 1,
            "&:hover": {
              backgroundColor: theme.palette.background.subContainer,
            },
          },
          "& .MuiAutocomplete-option:has(.MuiCheckbox-root)": {
            paddingLeft: 0.25,
            paddingTop: 0.25,
            paddingBottom: 0.25,
          },
        },
        ...listboxProps,
      }}
      sx={{
        width: width || (fullWidth ? "100%" : "auto"),
        "& .MuiAutocomplete-clearIndicator": {
          fontSize: "16px",
          padding: 0,
          marginRight: 0,
          "& svg": {
            fontSize: "16px",
          },
        },
        "& .MuiAutocomplete-popupIndicator": {
          padding: 0,
          marginRight: 0,
          "& svg": {
            fontSize: "16px",
          },
        },
        "& .MuiAutocomplete-endAdornment": {
          top: "50%",
          right: 0,
          transform: "translateY(-50%)",
        },
        ...sx,
      }}
    />
  );
};

CustomAutocomplete.propTypes = {
  options: PropTypes.array,
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  freeSolo: PropTypes.bool,
  disableClearable: PropTypes.bool,
  showCheckbox: PropTypes.bool,
  height: PropTypes.string,
  maxHeight: PropTypes.number,
  noOptionsText: PropTypes.string,
  loadingText: PropTypes.string,
  loading: PropTypes.bool,
  isOptionEqualToValue: PropTypes.func,
  filterOptions: PropTypes.func,
  renderOption: PropTypes.func,
  getOptionDisabled: PropTypes.func,
  groupBy: PropTypes.func,
  renderGroup: PropTypes.func,
  renderTags: PropTypes.func,
  sx: PropTypes.object,
  textFieldProps: PropTypes.object,
  listboxProps: PropTypes.object,
  listHeader: PropTypes.node,
  variant: PropTypes.oneOf(["outlined", "filled", "standard"]),
  size: PropTypes.oneOf(["small", "medium"]),
  fullWidth: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  width: PropTypes.string,
};

export default CustomAutocomplete;
